import { ErrorBoundary } from './components/error-boundary';
import { Skeleton } from './components/ui/skeleton';
import { type RouteItem, routes } from './confs/routes';
import TheMarketLayout from './layouts/market';
import { type FC, type PropsWithChildren, Suspense, isValidElement } from 'react';
import { Route, Switch } from 'wouter';

/** Default per-route fallback: a grid of card-shaped skeletons so navigating to
 *  a lazy route shows structure instead of a bare centered spinner. */
const RouteSkeleton = () => (
  <div className="mx-auto w-full max-w-[1200px] px-4 py-6 md:px-6">
    <Skeleton className="mb-8 h-10 w-64" />
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <Skeleton key={i} className="h-48 w-full" />
      ))}
    </div>
  </div>
);

/**
 * Recursively renders routes with their layouts and nested children
 * @param routes - Array of route configurations
 * @param parentPath - Parent path prefix for nested routes
 * @returns JSX Switch element containing all routes
 */
const renderRoutes = (routes: RouteItem[], parentPath = ''): JSX.Element => {
  const routeElements = routes.flatMap(route => {
    const {
      path,
      component,
      layout = ({ children }) => <TheMarketLayout>{children}</TheMarketLayout>,
      children,
      fallback = <RouteSkeleton />,
    } = route;
    // Normalize path by removing duplicate slashes
    const currentPath = `${parentPath}${path}`.replace(/\/+/g, '/');
    const routeElements: JSX.Element[] = [];
    // Recursively render child routes first
    if (children?.length) {
      const childRoutes = renderRoutes(children, currentPath);
      routeElements.push(...(childRoutes.props.children as JSX.Element[]));
    }
    // Create route element
    const RouteElement: FC = () => {
      const Layout = layout as FC<PropsWithChildren>;
      const Component = component as FC;
      return (
        <Layout>
          {/* Per-route boundary: a page that throws keeps the shell/nav and can
              be retried instead of white-screening the whole app. */}
          <ErrorBoundary resetKey={currentPath}>
            <Suspense fallback={fallback}>{isValidElement(component) ? (component as JSX.Element) : <Component />}</Suspense>
          </ErrorBoundary>
        </Layout>
      );
    };
    routeElements.push(<Route key={`route-${currentPath}`} path={currentPath} component={RouteElement} />);
    return routeElements;
  });

  return <Switch>{routeElements}</Switch>;
};

/**
 * Main application router component
 * @returns Rendered route switch with all application routes
 */
export const AppRouter: FC = () => {
  return renderRoutes(routes);
};

export default AppRouter;
